export const LEAVE_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'];

export const STATUS_COLORS = {
  PENDING:   '#f59e0b',
  APPROVED:  '#059669',
  REJECTED:  '#dc2626',
  CANCELLED: '#94a3b8',
};

const pad2 = (n) => String(n).padStart(2, '0');
const clean = (v) => (v === null || v === undefined ? '' : String(v).trim());

/** The API returns .NET dates like "8/21/2026 12:00:00 AM". */
export function parseApiDate(value) {
  const s = clean(value);
  if (!s) return null;

  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/i);
  if (m) {
    const [, mm, dd, yyyy, hh, min, sec, ampm] = m;
    let hour = hh ? parseInt(hh, 10) : 0;
    const period = (ampm || '').toUpperCase();
    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    return new Date(+yyyy, +mm - 1, +dd, hour, min ? +min : 0, sec ? +sec : 0);
  }
  const fallback = new Date(s);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

export const isoDate = (v) => {
  const d = parseApiDate(v);
  return d ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` : '';
};

export const displayDate = (v) => {
  const d = parseApiDate(v);
  return d ? `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}` : '—';
};

const days = (row) => Number(row?.HLF_TOTAL_DAYS) || 0;

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/* ── Leave types ─────────────────────────────────────────────────────────── */

/** id → { name, code, paid, active } for joining requests to their type. */
export function buildLeaveTypeMap(types = []) {
  const map = {};
  types.forEach((t) => {
    map[clean(t.HLT_TYPE_ID)] = {
      id: clean(t.HLT_TYPE_ID),
      code: clean(t.HLT_CODE),
      name: clean(t.HLT_NAME) || clean(t.HLT_CODE) || 'Unknown',
      paid: clean(t.HLT_IS_PAID).toLowerCase() === 'yes',
      active: clean(t.HLT_ACTIVE_STATUS).toLowerCase() === 'active',
      order: Number(t.HLT_DISPLAY_ORDER) || 0,
    };
  });
  return map;
}

export const countActiveTypes = (types = []) =>
  types.filter((t) => clean(t.HLT_ACTIVE_STATUS).toLowerCase() === 'active').length;

/* ── KPIs ────────────────────────────────────────────────────────────────── */

/**
 * @param {Object} byStatus  { PENDING: rows[], APPROVED: rows[], ... }
 */
export function computeLeaveKpis(byStatus = {}, types = [], holidays = []) {
  const rowsFor = (s) => byStatus[s] || [];
  const all = LEAVE_STATUSES.flatMap(rowsFor);

  const totalDays = all.reduce((sum, r) => sum + days(r), 0);
  const approvedRows = rowsFor('APPROVED');

  return {
    pending: rowsFor('PENDING').length,
    approved: approvedRows.length,
    rejected: rowsFor('REJECTED').length,
    cancelled: rowsFor('CANCELLED').length,
    totalRequests: all.length,
    totalDays,
    approvedDays: approvedRows.reduce((sum, r) => sum + days(r), 0),
    pendingDays: rowsFor('PENDING').reduce((sum, r) => sum + days(r), 0),
    avgDays: all.length ? totalDays / all.length : 0,
    employees: new Set(all.map((r) => clean(r.HLF_EMP_SERVICE_NO)).filter(Boolean)).size,
    onLeaveToday: countOnLeaveToday(approvedRows),
    activeTypes: countActiveTypes(types),
    upcomingHolidays: upcomingHolidays(holidays).length,
    approvalRate: (approvedRows.length + rowsFor('REJECTED').length)
      ? (approvedRows.length / (approvedRows.length + rowsFor('REJECTED').length)) * 100
      : 0,
  };
}

/** Distinct employees whose approved leave spans today. */
export function countOnLeaveToday(approvedRows = [], today = new Date()) {
  const t = startOfDay(today).getTime();
  const who = new Set();
  approvedRows.forEach((r) => {
    const from = parseApiDate(r.HLF_FROM_DATE);
    const to = parseApiDate(r.HLF_TO_DATE) || from;
    if (!from) return;
    if (startOfDay(from).getTime() <= t && t <= startOfDay(to).getTime()) {
      who.add(clean(r.HLF_EMP_SERVICE_NO));
    }
  });
  return who.size;
}

/* ── Charts ──────────────────────────────────────────────────────────────── */

/** Donut: how many requests sit in each status. */
export function buildStatusBreakdown(byStatus = {}) {
  return LEAVE_STATUSES
    .map((s) => ({
      name: s.charAt(0) + s.slice(1).toLowerCase(),
      value: (byStatus[s] || []).length,
      color: STATUS_COLORS[s],
    }))
    .filter((d) => d.value > 0);
}

/** Bar: total days taken per leave type (approved + pending). */
export function buildDaysByType(byStatus = {}, typeMap = {}) {
  const totals = {};
  ['APPROVED', 'PENDING'].forEach((s) => {
    (byStatus[s] || []).forEach((r) => {
      const t = typeMap[clean(r.HLF_LEAVE_TYPE_ID)];
      const label = t ? t.name : `Type ${clean(r.HLF_LEAVE_TYPE_ID) || '?'}`;
      totals[label] = (totals[label] || 0) + days(r);
    });
  });
  return Object.entries(totals)
    .map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }))
    .sort((a, b) => b.value - a.value);
}

/** Donut: paid vs no-pay days, using the leave type's HLT_IS_PAID. */
export function buildPaidSplit(byStatus = {}, typeMap = {}) {
  let paid = 0;
  let unpaid = 0;
  ['APPROVED', 'PENDING'].forEach((s) => {
    (byStatus[s] || []).forEach((r) => {
      const t = typeMap[clean(r.HLF_LEAVE_TYPE_ID)];
      if (t && !t.paid) unpaid += days(r);
      else paid += days(r);
    });
  });
  const round = (n) => Math.round(n * 100) / 100;
  return [
    { name: 'Paid', value: round(paid), color: '#059669' },
    { name: 'No Pay', value: round(unpaid), color: '#d97706' },
  ].filter((d) => d.value > 0);
}

/** Bar: requests per month, by applied date, oldest first. */
export function buildMonthlyTrend(byStatus = {}) {
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const buckets = {};
  LEAVE_STATUSES.flatMap((s) => byStatus[s] || []).forEach((r) => {
    const d = parseApiDate(r.HLF_APPLIED_DATE) || parseApiDate(r.HLF_FROM_DATE);
    if (!d) return;
    const key = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
    if (!buckets[key]) {
      buckets[key] = { key, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}`, requests: 0, days: 0 };
    }
    buckets[key].requests += 1;
    buckets[key].days += days(r);
  });
  return Object.values(buckets)
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((b) => ({ ...b, days: Math.round(b.days * 100) / 100 }));
}

/** Ranked list: employees who have taken/requested the most days. */
export function buildTopEmployees(byStatus = {}, limit = 6) {
  const totals = {};
  ['APPROVED', 'PENDING'].forEach((s) => {
    (byStatus[s] || []).forEach((r) => {
      const key = clean(r.HLF_EMP_SERVICE_NO) || 'Unknown';
      if (!totals[key]) totals[key] = { serviceNo: key, days: 0, requests: 0 };
      totals[key].days += days(r);
      totals[key].requests += 1;
    });
  });
  return Object.values(totals)
    .map((e) => ({ ...e, days: Math.round(e.days * 100) / 100 }))
    .sort((a, b) => b.days - a.days)
    .slice(0, limit);
}

/** The pending queue, soonest start date first — what needs action. */
export function buildPendingQueue(byStatus = {}, typeMap = {}, limit = 6) {
  return (byStatus.PENDING || [])
    .map((r) => ({
      formNo: clean(r.HLF_FORM_NO),
      serviceNo: clean(r.HLF_EMP_SERVICE_NO),
      typeName: (typeMap[clean(r.HLF_LEAVE_TYPE_ID)] || {}).name || '—',
      from: r.HLF_FROM_DATE,
      to: r.HLF_TO_DATE,
      days: days(r),
      reason: clean(r.HLF_REASON),
      halfDay: clean(r.HLF_HALF_DAY).toUpperCase() === 'Y',
      _sort: (parseApiDate(r.HLF_FROM_DATE) || new Date(8640000000000000)).getTime(),
    }))
    .sort((a, b) => a._sort - b._sort)
    .slice(0, limit);
}

/** Active holidays from today onwards, soonest first. */
export function upcomingHolidays(holidays = [], from = new Date(), limit = 100) {
  const t = startOfDay(from).getTime();
  return holidays
    .filter((h) => clean(h.HAH_IS_ACTIVE).toLowerCase() !== 'false')
    .map((h) => ({
      id: clean(h.HAH_HOLIDAY_ID),
      name: clean(h.HAH_HOLIDAY_NAME) || 'Holiday',
      type: clean(h.HAH_HOLIDAY_TYPE),
      remarks: clean(h.HAH_REMARKS),
      date: h.HAH_HOLIDAY_DATE,
      _t: (parseApiDate(h.HAH_HOLIDAY_DATE) || new Date(0)).getTime(),
    }))
    .filter((h) => h._t >= t)
    .sort((a, b) => a._t - b._t)
    .slice(0, limit);
}

/** Bar: holidays grouped by their type (Mercantile / Public / …). */
export function buildHolidayTypes(holidays = []) {
  const buckets = {};
  holidays
    .filter((h) => clean(h.HAH_IS_ACTIVE).toLowerCase() !== 'false')
    .forEach((h) => {
      const key = clean(h.HAH_HOLIDAY_TYPE) || 'Other';
      buckets[key] = (buckets[key] || 0) + 1;
    });
  return Object.entries(buckets)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

/** "2.5 days" / "1 day" */
export const fmtDays = (n) => {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return `${v} ${v === 1 ? 'day' : 'days'}`;
};